import { useEffect, useState } from 'react';
import { readSfxMuted, writeSfxMuted, readSfxVolume, writeSfxVolume } from './preferences.js';

export interface UseSfxSettingsReturn {
  /** `true` = SFX de UI silenciados. Default `false` (sonidos ENCENDIDOS). */
  muted: boolean;
  setMuted: (m: boolean) => void;
  toggle: () => void;
  /** Volumen 0..1. */
  volume: number;
  setVolume: (v: number) => void;
}

/**
 * Estado reactivo de los ajustes de SFX de UI (mute + volumen), device-local
 * (`orborix.sfxMuted` / `orborix.sfxVolume`), sincronizado entre
 * componentes/tabs vía los eventos `orborix:pref.*`. Para la UI de ajustes
 * (Perfil); el motor de SFX lee las prefs por su cuenta.
 */
export function useSfxSettings(): UseSfxSettingsReturn {
  const [muted, setMutedState] = useState<boolean>(() => readSfxMuted());
  const [volume, setVolumeState] = useState<number>(() => readSfxVolume());

  useEffect(() => {
    const onMuted = (e: Event): void => {
      const d = (e as CustomEvent<boolean>).detail;
      if (typeof d === 'boolean') setMutedState(d);
    };
    const onVolume = (e: Event): void => {
      const d = (e as CustomEvent<number>).detail;
      if (typeof d === 'number') setVolumeState(d);
    };
    window.addEventListener('orborix:pref.sfxMuted', onMuted);
    window.addEventListener('orborix:pref.sfxVolume', onVolume);
    return () => {
      window.removeEventListener('orborix:pref.sfxMuted', onMuted);
      window.removeEventListener('orborix:pref.sfxVolume', onVolume);
    };
  }, []);

  const setMuted = (m: boolean): void => {
    writeSfxMuted(m);
    setMutedState(m);
  };
  const setVolume = (v: number): void => {
    const clamped = Math.min(1, Math.max(0, v));
    writeSfxVolume(clamped);
    setVolumeState(clamped);
  };

  return { muted, setMuted, toggle: () => setMuted(!muted), volume, setVolume };
}
